<template>
  <Dialog v-model="dialogVisible" title="创建分销员" width="800">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="90"
    >
      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <el-form-item label="分销员" prop="userId">
            <el-select
              v-model="formData.userId"
              filterable
              remote
              reserve-keyword
              clearable
              class="w-1/1"
              placeholder="请输入用户编号或昵称，下拉选择要成为分销员的会员"
              :remote-method="(query) => debouncedSearchUser(query, 'user')"
              :loading="userSearchLoading"
              @change="(val) => handleUserSelected(val, 'user')"
            >
              <el-option
                v-for="item in userSearchOptions"
                :key="item.id"
                :label="`${item.id} - ${item.nickname || '未命名'}`"
                :value="item.id"
              />
            </el-select>
            <div class="el-form-item__tip">输入会员编号/昵称后请从下拉列表中选择，再点击「确定」创建分销员</div>
          </el-form-item>
          <!-- 展示分销员的信息 -->
          <el-descriptions v-if="userInfo.user" :column="1" border>
            <el-descriptions-item label="头像">
              <el-avatar :src="userInfo.user?.avatar" />
            </el-descriptions-item>
            <el-descriptions-item label="昵称">{{ userInfo.user?.nickname }}</el-descriptions-item>
          </el-descriptions>
        </el-col>

        <el-col :span="12" :xs="24">
          <el-form-item label="上级推广人" prop="bindUserId">
            <el-select
              v-model="formData.bindUserId"
              filterable
              remote
              reserve-keyword
              clearable
              class="w-1/1"
              placeholder="请输入已存在分销员的编号或昵称搜索"
              :remote-method="(query) => debouncedSearchUser(query, 'bindUser')"
              :loading="bindUserSearchLoading"
              @change="(val) => handleUserSelected(val, 'bindUser')"
            >
              <el-option
                v-for="item in bindUserSearchOptions"
                :key="item.id"
                :label="`${item.id} - ${item.nickname || '未命名'}`"
                :value="item.id"
              />
            </el-select>
            <div class="el-form-item__tip">上级推广人必须是已存在的分销员；从下拉列表中选择</div>
          </el-form-item>
          <!-- 展示上级推广人的信息 -->
          <el-descriptions v-if="userInfo.bindUser" :column="1" border>
            <el-descriptions-item label="头像">
              <el-avatar :src="userInfo.bindUser?.avatar" />
            </el-descriptions-item>
            <el-descriptions-item label="昵称">
              {{ userInfo.bindUser?.nickname }}
            </el-descriptions-item>
            <el-descriptions-item label="推广资格">
              <el-tag v-if="userInfo.bindUser?.brokerageEnabled">有</el-tag>
              <el-tag v-else type="info">无</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="成为推广员的时间">
              {{ formatDate(userInfo.bindUser?.brokerageTime) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as BrokerageUserApi from '@/api/mall/trade/brokerage/user'
import * as UserApi from '@/api/member/user'
import { formatDate } from '@/utils/formatTime'

defineOptions({ name: 'BrokerageUserCreateForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formData = ref({
  userId: undefined,
  bindUserId: undefined
})
const formRef = ref() // 表单 Ref
const formRules = reactive({
  userId: [{ required: true, message: '分销员不能为空', trigger: 'change' }],
  bindUserId: [{ required: true, message: '推广人不能为空', trigger: 'change' }]
})

const userSearchLoading = ref(false)
const bindUserSearchLoading = ref(false)
const userSearchOptions = ref<UserApi.UserVO[]>([])
const bindUserSearchOptions = ref<BrokerageUserApi.BrokerageUserVO[]>([])
let userSearchTimer: ReturnType<typeof setTimeout> | null = null
let bindUserSearchTimer: ReturnType<typeof setTimeout> | null = null

const debouncedSearchUser = (query: string, type: 'user' | 'bindUser') => {
  const timer = type === 'user' ? userSearchTimer : bindUserSearchTimer
  if (timer) clearTimeout(timer)
  const newTimer = setTimeout(() => searchUserOptions(query, type), 300)
  if (type === 'user') userSearchTimer = newTimer
  else bindUserSearchTimer = newTimer
}

const searchUserOptions = async (query: string, type: 'user' | 'bindUser') => {
  const keyword = String(query || '').trim()
  if (!keyword) {
    if (type === 'user') userSearchOptions.value = []
    else bindUserSearchOptions.value = []
    return
  }
  if (type === 'user') userSearchLoading.value = true
  else bindUserSearchLoading.value = true
  try {
    if (type === 'user') {
      const options: UserApi.UserVO[] = []
      if (/^\d+$/.test(keyword)) {
        try {
          const user = await UserApi.getUser(Number(keyword))
          if (user) options.push(user)
        } catch (e) {
          // 编号精确查找失败不影响昵称模糊搜索
        }
      }
      const page = await UserApi.getUserPage({ nickname: keyword, pageNo: 1, pageSize: 20 })
      ;(page.list || []).forEach((item) => {
        if (!options.some((o) => o.id === item.id)) options.push(item)
      })
      userSearchOptions.value = options
    } else {
      const options: BrokerageUserApi.BrokerageUserVO[] = []
      if (/^\d+$/.test(keyword)) {
        try {
          const user = await BrokerageUserApi.getBrokerageUser(Number(keyword))
          if (user) options.push(user)
        } catch (e) {
          // 同上
        }
      }
      const page = await BrokerageUserApi.getBrokerageUserPage({
        nickname: keyword,
        pageNo: 1,
        pageSize: 20
      })
      ;(page.list || []).forEach((item) => {
        if (!options.some((o) => o.id === item.id)) options.push(item)
      })
      bindUserSearchOptions.value = options
    }
  } catch (e: any) {
    if (type === 'user') userSearchOptions.value = []
    else bindUserSearchOptions.value = []
    const errMsg = e?.message || e?.msg || ''
    if (errMsg && !/服务器内部错误/.test(errMsg)) {
      message.error(errMsg)
    } else {
      message.error(type === 'user' ? '搜索会员失败' : '搜索推广员失败')
    }
  } finally {
    if (type === 'user') userSearchLoading.value = false
    else bindUserSearchLoading.value = false
  }
}

const handleUserSelected = async (id: any, type: 'user' | 'bindUser') => {
  if (!id) {
    if (type === 'user') userInfo.user = undefined
    else userInfo.bindUser = undefined
    return
  }
  if (type === 'user') {
    const user = await UserApi.getUser(id)
    if (!user) {
      message.warning('用户不存在')
      formData.value.userId = undefined
      userInfo.user = undefined
      return
    }
    userInfo.user = user
    return
  }
  if (id == formData.value.userId) {
    message.error('不能绑定自己为推广人')
    formData.value.bindUserId = undefined
    userInfo.bindUser = undefined
    return
  }
  const user = await BrokerageUserApi.getBrokerageUser(id)
  if (!user) {
    message.warning('推广员不存在')
    formData.value.bindUserId = undefined
    userInfo.bindUser = undefined
    return
  }
  userInfo.bindUser = user
}

/** 打开弹窗 */
const open = async () => {
  resetForm()
  dialogVisible.value = true
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
/** 创建分销员 */
const submitForm = async () => {
  if (formLoading.value) return
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  if (formData.value.userId == formData.value.bindUserId) {
    message.error('不能绑定自己为推广人')
    return
  }
  // 提交请求
  formLoading.value = true
  try {
    // 发起修改
    await BrokerageUserApi.createBrokerageUser(formData.value)
    message.success(t('common.createSuccess'))
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } catch (e: any) {
    // 业务异常 message 会被 axios 拦截器展示；这里兜底防止被洗成"服务器内部错误"
    const errMsg = e?.message || e?.msg || ''
    if (errMsg && !/服务器内部错误/.test(errMsg)) {
      message.error(errMsg)
    }
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formRef.value?.resetFields()
  formData.value = {
    userId: undefined,
    bindUserId: undefined
  }

  userInfo.bindUser = undefined
  userInfo.user = undefined
  userSearchOptions.value = []
  bindUserSearchOptions.value = []
  if (userSearchTimer) clearTimeout(userSearchTimer)
  if (bindUserSearchTimer) clearTimeout(bindUserSearchTimer)
}

/** 查询推广员和分销员 */
const userInfo = reactive<{
  bindUser: BrokerageUserApi.BrokerageUserVO | undefined
  user: UserApi.UserVO | undefined
}>({
  bindUser: undefined,
  user: undefined
})
</script>

<style scoped>
.el-form-item__tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
}
</style>
